<template>
  <div class="pricing-page white-theme">
    <div class="container">
      <div class="payment-error" v-if="paymentError">
        <span>{{ paymentError }}</span>
        <button class="error-close" @click="$store.dispatch('clearPaymentError')">×</button>
      </div>
      <div class="upgrade-success-toast" v-if="showUpgradeSuccessToast">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <span>{{ $t('pricing.upgradeSuccess') }}</span>
      </div>
      <div class="upgrade-error-toast" v-if="showUpgradeErrorToast">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        <span>{{ $t('pricing.upgradeFailed') }}</span>
      </div>
      <div class="pricing-header text-center">
        <h1 class="pricing-title">{{ $t('pricing.title') }}</h1>
        <p class="pricing-subtitle">{{ $t('pricing.subtitle') }}</p>
        <div class="pricing-header-controls">
          <div class="promo-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
            <span>{{ $t('pricing.promo') }}</span>
            <span class="promo-timer">{{ timerDisplay }}</span>
          </div>
          <div class="billing-toggle">
            <button :class="['toggle-btn', { active: billingCycle === 'monthly' }]" @click="setBilling('monthly')">{{ $t('pricing.monthly') }}</button>
            <button :class="['toggle-btn', { active: billingCycle === 'yearly' }]" @click="setBilling('yearly')">
              {{ $t('pricing.yearly') }} <span class="save-badge">-30%</span>
            </button>
          </div>
        </div>
        <p class="billing-note" v-if="billingCycle === 'yearly'">{{ $t('pricing.yearlyNote') }}</p>
      </div>

      <div class="plans-grid">
        <div v-for="plan in plans" :key="plan.id" :class="['plan-card', { popular: plan.popular, current: isCurrentPlan(plan) }]">
          <div class="plan-popular-tag" v-if="plan.popular && !isCurrentPlan(plan)">{{ $t('pricing.popular') }}</div>
          <div class="plan-current-tag" v-if="isCurrentPlan(plan)">{{ $t('pricing.currentPlan') }}</div>
          <h3 class="plan-name">{{ $t(plan.nameKey) }}</h3>
          <span class="plan-billing">{{ billingCycle === 'yearly' ? $t('pricing.yearly') + $t('pricing.subscribe') : $t('pricing.monthly') + $t('pricing.subscribe') }}</span>
          <p class="plan-desc">{{ $t('pricing.planDesc.' + plan.id) }}</p>
          <div class="plan-price">
            <span class="price-original" v-if="billingCycle === 'yearly'">${{ plan.monthlyPrice }}</span>
            <span class="price-current">${{ billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice }}</span>
            <span class="price-period">/month</span>
          </div>
          <div class="plan-total" v-if="billingCycle === 'yearly'">
            <span>{{ $t('pricing.yearlyBilling') }} ${{ plan.yearlyTotal }}</span>
            <span class="plan-saving">{{ $t('pricing.saveVsMonthly') }}${{ plan.monthlySaving }}</span>
          </div>
          <button 
            :class="['btn', plan.popular ? 'btn-primary' : 'btn-secondary', 'btn-lg']" 
            style="width:100%;justify-content:center;"
            :disabled="paymentLoading || isLowerPlan(plan) || isCurrentPlan(plan)"
            @click="handleSubscribe(plan)"
          >
            <span v-if="paymentLoading" class="loading-spinner"></span>
            {{ getButtonText(plan) }}
          </button>
          <div class="plan-credits">
            <div class="credits-main">{{ formatNumber(plan.credits) }} {{ $t('pricing.creditsPer') }} {{ $t('pricing.perMonth') }}</div>
            <div class="credits-bonus">+{{ formatNumber(plan.bonusCredits) }} {{ $t('pricing.extraCredits') }}</div>
            <div class="credits-rate">{{ $t('pricing.creditsRate') }}{{ plan.creditsPer100 }}</div>
          </div>
          <ul class="plan-features">
            <li v-for="(f, i) in plan.featuresKeys" :key="i">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
              {{ $t(f) }}
            </li>
          </ul>
        </div>

        <!-- Credits Pack -->
        <div class="plan-card credits-pack">
          <h3 class="plan-name">{{ $t('pricing.creditsPack.title') }}</h3>
          <span class="plan-billing">{{ $t('pricing.creditsPack.subtitle') }}</span>
          <div class="credits-select">
            <select v-model="selectedCredits" class="form-select">
              <option v-for="opt in creditOptions" :key="opt.credits" :value="opt">{{ formatNumber(opt.credits) }} credits</option>
            </select>
          </div>
          <div class="plan-price">
            <span class="price-current">${{ selectedCredits.price }}</span>
          </div>
          <p class="credits-note">{{ $t('pricing.creditsPack.oneTime') }}</p>
          <button 
            class="btn btn-secondary btn-lg" 
            style="width:100%;justify-content:center;"
            :disabled="paymentLoading"
            @click="handlePurchaseCredits"
          >
            <span v-if="paymentLoading" class="loading-spinner"></span>
            {{ paymentLoading ? $t('pricing.processing') : $t('pricing.creditsPack.buy') }}
          </button>
          <div class="plan-credits">
            <div class="credits-rate">{{ $t('pricing.creditsRate') }}{{ selectedCredits.rate }}</div>
            <div class="credits-note">{{ $t('pricing.creditsPack.permanent') }}</div>
          </div>
          <div class="credits-tip">
            <strong>{{ $t('pricing.creditsPack.tip') }}</strong>
            <ul>
              <li v-for="(item, idx) in $t('pricing.creditsPack.tipItems')" :key="idx">{{ item }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Security badges -->
      <div class="security-badges">
        <span class="security-badge">🛡️ {{ $t('pricing.security.noTraining') }}</span>
        <span class="security-badge">🔒 {{ $t('pricing.security.encrypted') }}</span>
        <span class="security-badge">✓ {{ $t('pricing.security.gdpr') }}</span>
        <span class="security-badge">🗑️ {{ $t('pricing.security.delete') }}</span>
      </div>
      <div class="text-center" style="margin-top:16px;">
        <router-link to="/trust-safety" class="link-accent">{{ $t('pricing.dataSafety') }}</router-link>
      </div>

      <!-- All Plans Include -->
      <div class="all-plans-section">
        <h2 class="text-center" style="margin-bottom:32px;">{{ $t('pricing.allPlansInclude') }}</h2>
        <div class="grid-4">
          <div class="include-item" v-for="inc in includes" :key="inc.key">
            <span class="include-icon">{{ inc.icon }}</span>
            <span>{{ $t('pricing.includes.' + inc.key) }}</span>
          </div>
        </div>
      </div>

      <!-- Credits Table -->
      <div class="credits-table-section">
        <button class="credits-table-toggle" @click="showCreditsTable = !showCreditsTable">
          <h3>{{ $t('pricing.creditsInfo.title') }}</h3>
          <span>{{ $t('pricing.creditsInfo.subtitle') }}</span>
          <svg :class="{ rotated: showCreditsTable }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        <div v-show="showCreditsTable" class="credits-table-content">
          <table class="credits-table">
            <thead>
              <tr><th>{{ $t('pricing.creditsInfo.model') }}</th><th>{{ $t('pricing.creditsInfo.type') }}</th><th>{{ $t('pricing.creditsInfo.cost') }}</th><th>{{ $t('pricing.creditsInfo.speed') }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="m in allModels" :key="m.id">
                <td>
                  <router-link :to="getModelLink(m)" class="table-link">{{ m.name }}</router-link>
                </td>
                <td>{{ m.category === 'flux' ? $t('pricing.creditsInfo.image') : (m.category === 'new' || m.category === 'other' ? $t('pricing.creditsInfo.video') : $t('pricing.creditsInfo.image')) }}</td>
                <td>{{ m.credits }} {{ $t('pricing.creditsPer') }}</td>
                <td>{{ m.speed }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pricing FAQ -->
      <div class="pricing-faq">
        <h2 class="text-center" style="margin-bottom:32px;">{{ $t('pricing.questions') }}</h2>
        <div class="faq-list">
          <div class="faq-item" v-for="(faq, idx) in pricingFaqs" :key="idx" :class="{ open: openFaq === idx }" @click="openFaq = openFaq === idx ? null : idx">
            <div class="faq-question">
              <span>{{ faq.q }}</span>
              <svg :class="{ rotated: openFaq === idx }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div class="faq-answer" v-show="openFaq === idx">
              <p>{{ faq.a }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="pricing-contact text-center">
        <p>{{ $t('pricing.contact') }} <a href="mailto:miral_support@miralvis.com" class="text-accent">miral_support@miralvis.com</a></p>
      </div>
    </div>

    <div class="upgrade-modal-overlay" v-if="showUpgradeModal" @click.self="cancelUpgrade">
      <div class="upgrade-modal">
        <h3 class="upgrade-modal-title">{{ $t('pricing.upgradeModal.title') }}</h3>
        <div class="upgrade-modal-content">
          <div class="upgrade-modal-item">
            <div class="upgrade-modal-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <p>{{ $t('pricing.upgradeModal.item1') }}</p>
          </div>
          <div class="upgrade-modal-item">
            <div class="upgrade-modal-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <p>{{ $t('pricing.upgradeModal.item2') }}</p>
          </div>
          <div class="upgrade-modal-item">
            <div class="upgrade-modal-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <p>{{ $t('pricing.upgradeModal.item3') }}</p>
          </div>
        </div>
        <div class="upgrade-modal-tip">
          <p>{{ $t('pricing.upgradeModal.tip') }}</p>
        </div>
        <div class="upgrade-modal-actions">
          <button class="btn btn-secondary" @click="cancelUpgrade">{{ $t('pricing.upgradeModal.cancel') }}</button>
          <button class="btn btn-primary" @click="confirmUpgrade">{{ $t('pricing.upgradeModal.confirm') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isAuthenticated } from '../api/auth'
import GA from '@/utils/ga'

export default {
  name: 'Pricing',
  data() {
    return {
      timer: { h: 20, m: 12, s: 51 },
      timerInterval: null,
      selectedCredits: null,
      showCreditsTable: false,
      openFaq: null,
      showUpgradeSuccessToast: false,
      showUpgradeErrorToast: false,
      showUpgradeModal: false,
      selectedPlan: null,
      includes: [
        { icon: '🎨', key: 'models' },
        { icon: '⚡', key: 'fast' },
        { icon: '🔒', key: 'privacy' },
        { icon: '📄', key: 'commercial' }
      ]
    }
  },
  computed: {
    plans() { return this.$store.state.plans },
    billingCycle() { return this.$store.state.billingCycle },
    allModels() { return this.$store.getters.allModels },
    creditOptions() { return this.$store.state.creditOptions },
    timerDisplay() {
      return `${String(this.timer.h).padStart(2, '0')}:${String(this.timer.m).padStart(2, '0')}:${String(this.timer.s).padStart(2, '0')}`
    },
    pricingFaqs() { return this.$t('pricing.faq.items') },
    paymentLoading() { return this.$store.state.paymentLoading },
    paymentError() { return this.$store.state.paymentError },
    upgradeSuccess() { return this.$store.state.upgradeSuccess },
    upgradeError() { return this.$store.state.upgradeError },
    userInfo() {
      if (this.$store.state.userInfo) {
        return this.$store.state.userInfo
      }
      try {
        var stored = localStorage.getItem('userInfo')
        if (stored) {
          var info = JSON.parse(stored)
          this.$store.commit('SET_USER_INFO', info)
          return info
        }
      } catch (e) {}
      return null
    },
    lastPay() {
      return this.userInfo ? this.userInfo.lastPay : null
    }
  },
  watch: {
    upgradeSuccess(newVal) {
      if (newVal) {
        this.showUpgradeSuccessToast = true
        this.$store.dispatch('clearUpgradeSuccess')
        var self = this
        setTimeout(function() {
          self.showUpgradeSuccessToast = false
        }, 3000)
      }
    },
    upgradeError(newVal) {
      if (newVal) {
        this.showUpgradeErrorToast = true
        this.$store.dispatch('clearUpgradeError')
        var self = this
        setTimeout(function() {
          self.showUpgradeErrorToast = false
        }, 3000)
      }
    }
  },
  created() {
    if (this.creditOptions && this.creditOptions.length > 0) {
      this.selectedCredits = this.creditOptions[0]
    }
  },
  methods: {
    getModelLink(m) {
      return '/models/' + (m.routeId || m.id)
    },
    setBilling(cycle) { this.$store.dispatch('setBillingCycle', cycle) },
    formatNumber(n) { return n.toLocaleString() },
    isCurrentPlan(plan) {
      if (!this.lastPay) return false
      if (this.lastPay.planName !== plan.planName) return false
      var currentBillingType = this.lastPay.pay_type
      var displayBillingCycle = this.billingCycle
      if (currentBillingType === '1') {
        return displayBillingCycle === 'yearly'
      } else if (currentBillingType === '2') {
        return displayBillingCycle === 'monthly'
      }
      return false
    },
    getPlanLevelKey(planName, billingCycle) {
      return planName + '_' + billingCycle
    },
    getCurrentPlanLevel() {
      if (!this.lastPay) return 0
      var billingType = this.lastPay.pay_type
      var billingCycle = billingType === '1' ? 'yearly' : 'monthly'
      var key = this.getPlanLevelKey(this.lastPay.planName, billingCycle)
      return this.$store.state.planLevels[key] || 0
    },
    getPlanLevel(plan) {
      var key = this.getPlanLevelKey(plan.planName, this.billingCycle)
      return this.$store.state.planLevels[key] || 0
    },
    isLowerPlan(plan) {
      if (!this.lastPay) return false
      var currentLevel = this.getCurrentPlanLevel()
      var planLevel = this.getPlanLevel(plan)
      return planLevel < currentLevel
    },
    isHigherPlan(plan) {
      if (!this.lastPay) return false
      var currentLevel = this.getCurrentPlanLevel()
      var planLevel = this.getPlanLevel(plan)
      return planLevel > currentLevel
    },
    getButtonText(plan) {
      if (this.paymentLoading) return this.$t('pricing.processing')
      if (this.isCurrentPlan(plan)) return this.$t('pricing.currentPlanBtn')
      if (this.isHigherPlan(plan)) return this.$t('pricing.upgrade')
      if (this.isLowerPlan(plan)) return this.$t('pricing.unavailable')
      return this.$t('pricing.subscribe')
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.timer.s > 0) { this.timer.s-- }
        else if (this.timer.m > 0) { this.timer.m--; this.timer.s = 59 }
        else if (this.timer.h > 0) { this.timer.h--; this.timer.m = 59; this.timer.s = 59 }
      }, 1000)
    },
    checkAuth() {
      if (!isAuthenticated()) {
        this.$store.dispatch('showLoginModal')
        return false
      }
      return true
    },
    handleSubscribe(plan) {
      if (!this.checkAuth()) return
      GA.subscribeClick(this.userInfo ? this.userInfo.id : '', this.billingCycle, plan.name)
      var isUpgrade = this.isHigherPlan(plan)
      if (isUpgrade) {
        this.selectedPlan = plan
        this.showUpgradeModal = true
      } else {
        this.$store.dispatch('subscribeToPlan', { plan: plan, isUpgrade: false })
      }
    },
    confirmUpgrade() {
      if (this.selectedPlan) {
        this.showUpgradeModal = false
        this.$store.dispatch('subscribeToPlan', { plan: this.selectedPlan, isUpgrade: true })
      }
    },
    cancelUpgrade() {
      this.showUpgradeModal = false
      this.selectedPlan = null
    },
    handlePurchaseCredits() {
      if (!this.checkAuth()) return
      this.$store.dispatch('purchaseCredits', this.selectedCredits)
    }
  },
  mounted() {
    this.$store.commit('SET_PAYMENT_LOADING', false)
    this.startTimer()
    // 处理浏览器 bfcache：当用户从 Stripe 返回时重置加载状态
    var self = this
    this.bfcacheHandler = function(evt) {
      if (evt.persisted) {
        self.$store.commit('SET_PAYMENT_LOADING', false)
      }
    }
    window.addEventListener('pageshow', this.bfcacheHandler)
  },
  beforeDestroy() {
    clearInterval(this.timerInterval)
    if (this.bfcacheHandler) {
      window.removeEventListener('pageshow', this.bfcacheHandler)
    }
  }
}
</script>

<style scoped>
.pricing-page { padding: 48px 0 80px; }
.pricing-header { margin-bottom: 48px; }
.pricing-title {
  font-size: 40px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--accent), #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 12px;
}
.pricing-subtitle { color: var(--text-secondary); font-size: 16px; margin-bottom: 24px; }
.pricing-header-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.promo-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  background: var(--accent-light);
  border: 1px solid var(--accent-border);
  border-radius: var(--radius-full);
  font-size: 14px;
  color: var(--accent);
  margin-bottom: 0;
}
.promo-timer { font-weight: 700; font-variant-numeric: tabular-nums; }
.billing-toggle {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-full);
  margin-bottom: 0;
}
.toggle-btn {
  padding: 10px 28px;
  border-radius: var(--radius-full);
  font-size: 14px;
  color: var(--text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.toggle-btn.active { background: var(--bg-primary); color: var(--text-primary); font-weight: 600; }
.save-badge {
  padding: 2px 6px;
  background: #00D97E;
  color: #000;
  border-radius: var(--radius-full);
  font-size: 11px;
  font-weight: 700;
}
.billing-note { font-size: 13px; color: var(--text-muted); margin-top: 8px; }

.plans-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 48px;
}
.plan-card {
  background: var(--bg-card);
  border: 1px solid var(--bg-card-border);
  border-radius: var(--radius-xl);
  padding: 28px;
  position: relative;
}
.plan-card.popular { border-color: #00b96b; }
.plan-card.current { border-color: #3b82f6; border-width: 2px; }
.plan-popular-tag {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 16px;
  background: #00D97E;
  color: #000;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 700;
}
.pricing-page .btn.btn-primary.btn-lg {
  background: #00D97E;
}
.pricing-page .btn.btn-primary.btn-lg:hover {
  background: #00D97E;
}
.plan-current-tag {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 16px;
  background: #3b82f6;
  color: #fff;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 700;
}
.plan-name { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
.plan-billing { font-size: 12px; color: var(--accent); }
.plan-desc { font-size: 13px; color: var(--text-muted); margin: 8px 0 16px; }
.plan-price { margin-bottom: 8px; }
.price-original { text-decoration: line-through; color: var(--text-muted); font-size: 16px; margin-right: 4px; }
.price-current { font-size: 36px; font-weight: 800; }
.price-period { font-size: 14px; color: var(--text-muted); }
.plan-total { font-size: 12px; color: var(--text-muted); margin-bottom: 16px; }
.plan-saving { display: block; color: var(--accent); font-weight: 500; }
.plan-credits { margin-top: 16px; padding: 12px; background: rgba(255,255,255,0.02); border-radius: var(--radius-md); }
.credits-main { font-size: 15px; font-weight: 700; }
.credits-bonus { font-size: 12px; color: var(--accent); }
.credits-rate { font-size: 12px; color: var(--text-muted); margin-top: 4px; }
.plan-features { margin-top: 16px; }
.plan-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: var(--text-secondary);
}
.credits-pack { }
.form-select {
  width: 100%;
  padding: 10px 14px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  margin-bottom: 16px;
  outline: none;
}
.credits-note { font-size: 12px; color: var(--text-muted); margin-bottom: 12px; }
.credits-tip {
  margin-top: 16px;
  padding: 12px;
  background: rgba(0,217,126,0.05);
  border: 1px solid var(--accent-border);
  border-radius: var(--radius-md);
  font-size: 12px;
  color: var(--text-secondary);
}
.credits-tip strong { color: var(--text-primary); display: block; margin-bottom: 4px; }
.credits-tip ul { padding-left: 16px; list-style: disc; }
.credits-tip li { margin: 2px 0; }

.security-badges {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 32px;
}
.security-badge {
  padding: 8px 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-full);
  font-size: 13px;
  color: var(--text-secondary);
}
.link-accent { color: var(--accent); font-size: 13px; }
.link-accent:hover { text-decoration: underline; }

.upgrade-success-toast {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  background: var(--accent);
  color: #000;
  border-radius: var(--radius-lg);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1000;
  animation: slideDown 0.3s ease;
}
.upgrade-error-toast {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  background: #ef4444;
  color: #fff;
  border-radius: var(--radius-lg);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1000;
  animation: slideDown 0.3s ease;
}
@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.upgrade-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.upgrade-modal {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: 32px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}
.upgrade-modal-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-align: center;
}
.upgrade-modal-content {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 20px;
}
.upgrade-modal-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}
.upgrade-modal-item:last-child {
  margin-bottom: 0;
}
.upgrade-modal-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #10b981;
}
.upgrade-modal-item p {
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
}
.upgrade-modal-tip {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: var(--radius-lg);
  padding: 12px;
  margin-bottom: 20px;
}
.upgrade-modal-tip p {
  font-size: 12px;
  color: #60a5fa;
}
.upgrade-modal-actions {
  display: flex;
  gap: 12px;
}
.upgrade-modal-actions .btn {
  flex: 1;
  justify-content: center;
}

.all-plans-section { margin-top: 64px; }
.include-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 500;
}
.include-icon { font-size: 20px; }

.credits-table-section { margin-top: 48px; }
.credits-table-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  cursor: pointer;
}
.credits-table-toggle h3 { font-size: 16px; }
.credits-table-toggle span { font-size: 13px; color: var(--text-muted); }
.credits-table-toggle svg { transition: transform 0.2s; }
.credits-table-toggle svg.rotated { transform: rotate(180deg); }
.credits-table-content { margin-top: 12px; }
.credits-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-card);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.credits-table th, .credits-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--border-color);
  font-size: 13px;
}
.credits-table th { background: rgba(255,255,255,0.03); font-weight: 600; color: var(--text-secondary); }
.table-link { color: var(--accent); }
.table-link:hover { text-decoration: underline; }

.pricing-faq { margin-top: 64px; }
.faq-list { max-width: 700px; margin: 0 auto; }
.faq-item {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  margin-bottom: 8px;
  cursor: pointer;
}
.faq-item:hover { border-color: #333; }
.faq-item.open { border-color: var(--accent-border); }
.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  font-size: 15px;
  font-weight: 500;
}
.faq-question svg { transition: transform 0.2s; color: var(--text-muted); }
.faq-question svg.rotated { transform: rotate(180deg); }
.faq-answer { padding: 0 20px 16px; }
.faq-answer p { font-size: 14px; color: var(--text-secondary); line-height: 1.7; }

.pricing-contact { margin-top: 48px; font-size: 14px; color: var(--text-muted); }

.payment-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 82, 82, 0.1);
  border: 1px solid rgba(255, 82, 82, 0.3);
  border-radius: var(--radius-md);
  color: #ff5252;
  font-size: 14px;
  margin-bottom: 24px;
}
.error-close {
  background: none;
  border: none;
  color: #ff5252;
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}
.error-close:hover { opacity: 0.7; }

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 1024px) {
  .plans-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .plans-grid { grid-template-columns: 1fr; }
  .pricing-title { font-size: 28px; }
}
</style>
